import moment from 'moment';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmissionError, reset, initialize, formValueSelector } from 'redux-form';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { data } from 'services';
import { path } from 'router/sections';
import { addTariff, getTariffTemplates } from 'services/tariffs-service';
import { PageWrapper, CloseCardButton, HeadLineWithMargin, Card, GeneralInfo, DataWrapper } from 'libs/ui';
import { getTarrificatorByPath, getCategories, getVehicleClasses } from 'services/types';
import { NewTariffForm } from './forms/new-tariff-form';
import { TarifficatorSelector } from './blocks/tarifficator-selector';
import { prepareFormInitial, prepareTariffForSave } from './tariff.config';
import { HeaderText, TariffManageWrapper } from './tariff-card-new.styled';

const formName = 'newTariffForm';
const selector = formValueSelector(formName);

const initial = {
  type: 'individual',
  time_to_leave: 0,
  time_from: moment(),
  subscription_period: 0,
  subscription_period_unit: 'days',
};

export const NewTariffCard = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const name1 = useSelector((store) => selector(store, 'name'));
  const vehicle_class1 = useSelector((store) => selector(store, 'vehicle_class'));
  const secondentry_interval1 = useSelector((store) => selector(store, 'secondentry_interval'));
  const subscription_period1 = useSelector((store) => selector(store, 'subscription_period'));
  const secondentry_period1 = useSelector((store) => selector(store, 'secondentry_period'));
  const time_limit1 = useSelector((store) => selector(store, 'time_limit'));

  const dispatch = useDispatch();
  const zones = data.getDefaultZones();

  const [resetn, setResetn] = useState(false);
  const [zone, setZone] = useState(Object.keys(zones)[0]);
  const [tarifficatorType, setTarifficatorType] = useState('');
  const [template, setTemplate] = useState('');
  const [templates, setTemplates] = useState([]);
  const [defaultVals, setDefaultVals] = useState(undefined);

  /**
   * prepare initial data
   */
  useEffect(() => {
    let _mounted = true;

    // get tariff data
    const getData = async () => {
      let err = false;

      // get tarifficators
      let templates = await getTariffTemplates().catch(() => {
        err = true;
      });
      if (err) {
        templates = [];
      }

      // init
      if (!err) {
        let def = templates.filter((el) => {
          return el.default;
        });
        if (!def.length) {
          def = templates.length ? [templates[0]] : [{}];
        }
        if (_mounted) {
          setTemplates(templates);
          setTemplate(def[0]);
          setTarifficatorType(getTarifficatorType(def[0]));
        }
      }
    };

    // get data from api
    getData();

    return () => {
      _mounted = false;
    };
  }, []);

  /**
   * set defaults by tarifficatorType
   */
  useEffect(() => {
    if (tarifficatorType) {
      const data = prepareInitial(template);
      setDefaultVals(data);
      dispatch(reset(formName));
      dispatch(
        initialize(
          formName,
          {
            ...initial,
            name: name1,
            vehicle_class: vehicle_class1,
            secondentry_interval: secondentry_interval1,
            secondentry_period: secondentry_period1,
            subscription_period: subscription_period1,
            time_limit: time_limit1,
            zones: data.zones,
          },
          false,
          { updateUnregisteredFields: false }
        )
      );
    }
  }, [tarifficatorType]);

  /**
   * get tarifficator Type
   * @param {*} template
   * @returns
   */
  const getTarifficatorType = (template) => {
    return getTarrificatorByPath(template.path, template.template);
  };

  /**
   * get initial from selected template
   * @param {*} template
   * @returns
   */
  const prepareInitial = (template) => {
    // empty
    if (!template) {
      return {};
    }

    // data
    const data = prepareFormInitial(template.data, tarifficatorType);
    return data;
  };

  /**
   * save individual tariff
   * @param {*} values
   * @returns
   */
  const submit = (values) => {
    const out = prepareTariffForSave(values, tarifficatorType);

    out.tarifficator_cls = template?.path;
    out.tarifficator = template?.template;

    // test
    const test = false;
    if (test) {
      console.log('🚀 ~ new tariff:', out);
      return Promise.resolve(true);
    }

    // add
    return addTariff(out)
      .then((data) => {
        afterSubmit(data.id);
      })
      .catch((error) => {
        const errors = {};
        if (Array.isArray(error)) {
          error.forEach((element) => {
            if (element.field) {
              errors[element.field] = element.type;
              errors._error = element.type;
            }
            if (element.param) {
              errors[element.param] = element.message;
              errors._error = element.message;
            }
          });
        } else {
          errors[error.field] = error.type;
          errors._error = error.type;
        }

        throw new SubmissionError({ ...errors });
      });
  };

  /**
   * after submit
   * @param {*} id
   */
  const afterSubmit = (id) => {
    history.push(`${path('tariff')}?id=${id}&add=1`);
  };

  /**
   * close card
   */
  const closeCard = () => {
    history.push(path('tariffs'));
  };

  /**
   * get initial data
   * @returns
   */
  const getInitial = () => {
    const out = { ...initial, ...(defaultVals ?? {}) };
    return out;
  };

  /**
   * change tarifficator
   * @param {*} path
   */
  const changeTarifficator = (path) => {
    const template = templates.find((el) => el.path === path);
    setTemplate(template);
    setTarifficatorType(getTarifficatorType(template));
    setZone(Object.keys(zones)[0]);
    // setResetn((val) => !val);
  };

  // render
  if (defaultVals === undefined || !tarifficatorType) {
    return null;
  }

  const categories = getCategories();
  const vehicleClasses = getVehicleClasses();

  const tarrificatorParams = {
    selectedZone: zone,
    setZone,
    zones,
    disabled: false,
    newIndividual: true,
    template: defaultVals,
    info: defaultVals,
    tarifficatorType,
  };

  const initialValues = getInitial();
  // console.log('🚀 ~ NewTariffCard ~ initialValues:', initialValues.zones.P1);

  return (
    <PageWrapper>
      <HeadLineWithMargin>
        <p>{t('form.Карточка тарифа')}</p>
        <CloseCardButton close={closeCard} />
      </HeadLineWithMargin>
      <Card>
        <GeneralInfo className="new-tariff">
          <HeaderText>{t('form.Новый тариф')}</HeaderText>
          <TarifficatorSelector onChange={changeTarifficator} items={templates} value={template} />
        </GeneralInfo>
        <DataWrapper>
          <TariffManageWrapper>
            <NewTariffForm
              onSubmit={submit}
              initialValues={initialValues}
              tarrificatorParams={tarrificatorParams}
              vehicleClasses={vehicleClasses}
              categories={categories}
              resetn={resetn}
            />
          </TariffManageWrapper>
        </DataWrapper>
      </Card>
    </PageWrapper>
  );
};
